import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuthState } from "react-firebase-hooks/auth";
import auth from "../../../firebase.init";
import { toast } from "react-hot-toast";
import {
  Eye,
  EyeOff,
  Save,
  Trash2,
  ShieldCheck,
  Loader2,
  RefreshCw,
  Key,
  Settings,
  ExternalLink,
} from "lucide-react";
import { Modal } from "react-bootstrap";

const textModelOptions = [
  "gemini-2.5-flash",
  "gemini-2.5-flash-lite",
  "gemini-2.5-pro",
  "gemini-2.0-flash",
];

const imageModelOptions = [
  "gemini-2.0-flash-exp-image-generation",
  "gemini-2.5-flash-image",
];

const GeminiKeyManager = () => {
  const [user] = useAuthState(auth);
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [hasKey, setHasKey] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [textModel, setTextModel] = useState(
    localStorage.getItem("geminiTextModel") || "gemini-2.5-flash",
  );
  const [imageModel, setImageModel] = useState(
    localStorage.getItem("geminiImageModel") ||
      "gemini-2.0-flash-exp-image-generation",
  );

  useEffect(() => {
    if (user?.email) {
      fetchUserKey();
    }
  }, [user]);

  const fetchUserKey = async () => {
    setIsFetching(true);
    try {
      const res = await axios.get(
        `https://server.enjoywiki.com/youtubeai-may/api/user-gemini-key?email=${user.email}`,
      );
      if (res.data.success && res.data.key) {
        setApiKey(res.data.key);
        setHasKey(true);
      } else {
        setHasKey(false);
      }
    } catch (error) {
      console.error("Error fetching Gemini key:", error);
    } finally {
      setIsFetching(false);
    }
  };

  const handleSave = async () => {
    if (!apiKey.trim()) {
      toast.error("Please enter a valid API key");
      return;
    }

    if (!apiKey.startsWith("AIza") && !apiKey.startsWith("AQ.")) {
      toast.error("Invalid key format (Must start with AIza or AQ.)");
      return;
    }

    setIsLoading(true);
    try {
      const res = await axios.post(
        `https://server.enjoywiki.com/youtubeai-may/api/user-gemini-key`,
        {
          email: user.email,
          key: apiKey,
        },
      );
      if (res.data.success) {
        localStorage.setItem("geminiTextModel", textModel);
        localStorage.setItem("geminiImageModel", imageModel);
        toast.success(hasKey ? "Key updated" : "Key saved");
        setHasKey(true);
        setShowModal(false);
      }
    } catch (error) {
      toast.error("Failed to save key");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete your Gemini API key?")) return;

    setIsLoading(true);
    try {
      const res = await axios.delete(
        `https://server.enjoywiki.com/youtubeai-may/api/user-gemini-key?email=${user.email}`,
      );
      if (res.data.success) {
        toast.success("Key deleted");
        setApiKey("");
        setHasKey(false);
        setShowModal(false);
      }
    } catch (error) {
      toast.error("Failed to delete key");
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) return null;

  return (
    <>
      <div className="col-lg-3 col-md-4 col-sm-6">
        <div
          className="saas-card h-100 p-4 position-relative overflow-hidden text-center"
          onClick={() => setShowModal(true)}
          style={{
            background: "#161625",
            borderRadius: "10px",
            border: "1px solid rgba(255,255,255,0.05)",
            transition: "all 0.3s ease",
            minHeight: "220px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            cursor: "pointer",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-5px)";
            e.currentTarget.style.borderColor = "#4285F4";
            e.currentTarget.style.boxShadow = `0 10px 20px -10px #4285F440`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.borderColor = "rgba(255,255,255,0.05)";
            e.currentTarget.style.boxShadow = "none";
          }}
        >
          {/* Top decorative bar */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "4px",
              background: "#4285F4",
            }}
          ></div>

          <div className="saas-icon-wrapper mb-3">
            <Key size={40} style={{ color: "#4285F4" }} />
          </div>

          <h5 className="mb-2 fw-bold text-white">Gemini API Settings</h5>
          <p className="text-secondary small mb-0" style={{ fontSize: "11px" }}>
            {hasKey
              ? "Key configured. Click to manage."
              : "Click to connect your personal API key."}
          </p>

          {hasKey && (
            <div className="mt-2">
              <span
                className="badge bg-success-soft text-success small fw-normal"
                style={{ fontSize: "9px" }}
              >
                Active
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Gemini Settings Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        contentClassName="gemini-modal-content"
      >
        <Modal.Header closeButton className="border-0 bg-transparent pb-0">
          <Modal.Title className="text-white fs-5 fw-bold d-flex align-items-center gap-2">
            <Settings size={20} className="text-primary" />
            Configure Gemini API
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 pt-3">
          <p className="text-secondary small mb-4">
            Enter your personal Google Gemini API key to use the AI tools with
            your own quota. You can get a free key from the{" "}
            <a
              href="https://aistudio.google.com/app/apikey"
              target="_blank"
              rel="noreferrer"
              className="text-primary text-decoration-none"
            >
              Google AI Studio <ExternalLink size={12} />
            </a>
            .
          </p>

          <div className="form-group mb-4">
            <label className="text-white small mb-2 fw-bold d-flex align-items-center gap-2">
              Your API Key
              <ShieldCheck
                size={14}
                className="text-success"
                title="Encrypted Storage"
              />
            </label>
            <div className="position-relative">
              <input
                type={showKey ? "text" : "password"}
                className="form-control"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Enter API Key (AIza... or AQ...)"
                style={{
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  color: "white",
                  padding: "12px 15px",
                  borderRadius: "8px",
                  paddingRight: "45px",
                }}
              />
              <button
                className="position-absolute end-0 top-50 translate-middle-y border-0 bg-transparent text-secondary px-3"
                onClick={() => setShowKey(!showKey)}
                style={{ zIndex: 10, outline: "none" }}
              >
                {showKey ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="d-flex flex-column gap-3">
            <button
              className="btn btn-primary w-100 d-flex align-items-center justify-content-center gap-2 py-2 fw-bold"
              onClick={handleSave}
              disabled={isLoading}
              style={{ borderRadius: "8px" }}
            >
              {isLoading ? (
                <Loader2 className="animate-spin" size={18} />
              ) : hasKey ? (
                <RefreshCw size={18} />
              ) : (
                <Save size={18} />
              )}
              {hasKey ? "Update Configuration" : "Save Configuration"}
            </button>

            {hasKey && (
              <button
                className="btn btn-link text-danger text-decoration-none small d-flex align-items-center justify-content-center gap-1"
                onClick={handleDelete}
                disabled={isLoading}
              >
                <Trash2 size={14} />
                Delete API Key
              </button>
            )}
          </div>

          <div className="mt-4 form-group">
            <label className="text-white small mb-2 fw-bold d-flex align-items-center gap-2">
              Text Generation Model
            </label>
            <select
              className="form-select form-control mb-2"
              value={
                textModelOptions.includes(textModel) ? textModel : "custom"
              }
              onChange={(e) => {
                if (e.target.value !== "custom") {
                  setTextModel(e.target.value);
                } else {
                  setTextModel("");
                }
              }}
              style={{
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "white",
                padding: "12px 15px",
                borderRadius: "8px",
                appearance: "auto",
              }}
            >
              {textModelOptions.map((opt) => (
                <option key={opt} value={opt} style={{ color: "black" }}>
                  {opt}
                </option>
              ))}
              <option value="custom" style={{ color: "black" }}>
                Custom Model...
              </option>
            </select>
            {!textModelOptions.includes(textModel) && (
              <input
                type="text"
                className="form-control mb-4"
                value={textModel}
                onChange={(e) => setTextModel(e.target.value)}
                placeholder="Enter custom text model"
                style={{
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  color: "white",
                  padding: "12px 15px",
                  borderRadius: "8px",
                }}
              />
            )}
            {textModelOptions.includes(textModel) && (
              <div className="mb-4"></div>
            )}

            <label className="text-white small mb-2 fw-bold d-flex align-items-center gap-2">
              Image Generation Model
            </label>
            <select
              className="form-select form-control mb-2"
              value={
                imageModelOptions.includes(imageModel) ? imageModel : "custom"
              }
              onChange={(e) => {
                if (e.target.value !== "custom") {
                  setImageModel(e.target.value);
                } else {
                  setImageModel("");
                }
              }}
              style={{
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "white",
                padding: "12px 15px",
                borderRadius: "8px",
                appearance: "auto",
              }}
            >
              {imageModelOptions.map((opt) => (
                <option key={opt} value={opt} style={{ color: "black" }}>
                  {opt}
                </option>
              ))}
              <option value="custom" style={{ color: "black" }}>
                Custom Model...
              </option>
            </select>
            {!imageModelOptions.includes(imageModel) && (
              <input
                type="text"
                className="form-control mb-2"
                value={imageModel}
                onChange={(e) => setImageModel(e.target.value)}
                placeholder="Enter custom image model"
                style={{
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  color: "white",
                  padding: "12px 15px",
                  borderRadius: "8px",
                }}
              />
            )}
          </div>

          <div
            className="mt-4 p-3 rounded-3"
            style={{
              background: "rgba(255,255,255,0.02)",
              border: "1px solid rgba(255,255,255,0.05)",
            }}
          >
            <div className="d-flex gap-2">
              <ShieldCheck size={20} className="text-success flex-shrink-0" />
              <div>
                <p className="text-white small mb-1 fw-bold">
                  Security & Privacy
                </p>
                <p className="text-secondary mb-0" style={{ fontSize: "11px" }}>
                  Your key is encrypted using AES-256-CBC and stored securely.
                  It is only used to make requests on your behalf and is never
                  shared.
                </p>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <style jsx global>{`
        .gemini-modal-content {
          background: #161625 !important;
          border: 1px solid rgba(255, 255, 255, 0.1) !important;
          border-radius: 16px !important;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5) !important;
        }
        .gemini-modal-content .btn-close {
          filter: invert(1) grayscale(100%) brightness(200%);
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        .bg-success-soft {
          background: rgba(40, 167, 69, 0.1);
          border: 1px solid rgba(40, 167, 69, 0.2);
        }
        .btn-primary {
          background: #4285f4 !important;
          border: none !important;
        }
        .btn-primary:hover {
          background: #3367d6 !important;
        }
      `}</style>
    </>
  );
};

export default GeminiKeyManager;
